<template>
  <div class="report-dashboard" :class="{ 'theme-light': themeLight }">
    <div v-if="reportVins.length > 1" class="vehicle-tabs no-print">
      <button
        v-for="vin in reportVins"
        :key="vin"
        class="vehicle-tab"
        :class="{ active: vin === store.activeReportVin }"
        @click="store.setActiveReportVin(vin)"
      >
        {{ tabLabel(vin) }}
      </button>
    </div>

    <section class="header-card">
      <button class="theme-pill no-print" @click="toggleTheme()">
        {{ themeLight ? '🌙 ' + t('report.themeDark') : '☀️ ' + t('report.themeLight') }}
      </button>
      <span class="eyebrow">{{ t('report.title') }}</span>
      <h2 class="report-title">
        {{ report?.vehicle?.year || store.vehicleData?.year || t('report.notAvailable') }}
        {{ report?.vehicle?.make || store.vehicleData?.make || '' }}
        {{ report?.vehicle?.model || store.vehicleData?.model || '' }}
      </h2>
      <div class="meta-line">
        <span>{{ t('report.region') }}: {{ report?.vehicle?.region || store.vehicleData?.region || t('report.unknown') }}</span>
        <span>VIN: {{ report?.vehicleData?.vin || store.vehicleData?.vin || t('report.notAvailable') }}</span>
        <span v-if="sessionExpiryLabel">{{ t('report.session') }}: {{ sessionExpiryLabel }}</span>
      </div>
      <p class="lead">
        {{ reportLead }}
      </p>
      <div class="action-row no-print">
        <div class="button-row">
          <label
            class="button ghost report-upload-button"
            :class="{ disabled: !reportVin || currentPhotos.length >= 10 }"
            :title="currentPhotos.length >= 10 ? t('report.maxPhotosReached') : t('report.uploadPhotosHint')"
          >
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              multiple
              :disabled="!reportVin || currentPhotos.length >= 10"
              @change="handlePhotoUpload"
              class="photo-file-input"
            />
            {{ t('report.vehiclePhotos') }} ({{ currentPhotos.length }}/10)
          </label>
        </div>
        <div class="button-row pdf-zip-primary-row">
          <button
            class="button primary report-progress-wrapper"
            :disabled="!pdfZipSummary.canDownload || isZipRunning || !store.sessionId"
            :aria-busy="isZipRunning ? 'true' : 'false'"
            @click="handleDownloadAllPdfs"
          >
            <span v-if="isZipRunning" class="report-progress-bar" :style="{ width: `${zipProgress}%` }"></span>
            <span class="report-progress-content">
              <span v-if="isZipRunning">{{ t('actions.downloadingAllPdfs') }} {{ zipProgress }}%</span>
              <span v-else>{{ t('actions.downloadAllPdfs') }}</span>
            </span>
          </button>
          <p class="pdf-zip-summary" v-if="pdfZipSummary.canDownload">
            {{ t('report.pdfZip.summary', {
              reports: pdfZipSummary.reports,
              invoices: pdfZipSummary.invoices,
              months: pdfZipSummary.months,
              receipt: pdfZipSummary.hasReceipt ? t('report.pdfZip.receiptYes') : t('report.pdfZip.receiptNo')
            }) }}
          </p>
          <p class="pdf-zip-summary muted" v-else>{{ t('report.pdfZip.empty') }}</p>
          <p class="pdf-zip-summary alert-inline" v-if="zipError">{{ zipError }}</p>
        </div>
        <details class="more-downloads no-print">
          <summary>{{ t('actions.moreDownloads') }}</summary>
          <div class="button-row">
            <a v-if="chargingCsvUrl" :href="chargingCsvUrl" target="_blank" rel="noopener noreferrer" class="button ghost">
              {{ t('report.downloadChargingCsv') }}
            </a>
            <a v-if="sessionInvoiceZipUrl" :href="sessionInvoiceZipUrl" target="_blank" rel="noopener noreferrer" class="button ghost">
              {{ t('actions.downloadInvoices') }}
            </a>
            <button
              v-if="store.paymentId"
              class="button ghost"
              :disabled="isDownloadingReceipt || receiptDownloaded"
              @click="handleDownloadReceipt"
            >
              {{ isDownloadingReceipt ? t('actions.downloadingReceipt') : t('actions.downloadReceipt') }}
            </button>
            <button
              class="button ghost report-progress-wrapper"
              :disabled="isGeneratingPdf || !report"
              @click="handleSavePdf"
            >
              <span v-if="isGeneratingPdf" class="report-progress-bar" :style="{ width: `${pdfProgress}%` }"></span>
              <span class="report-progress-content">
                <span v-if="isGeneratingPdf">{{ pdfProgress < 30 ? t('actions.generatingPdf') : `${t('actions.generatingPdf')} ${pdfProgress}%` }}</span>
                <span v-else>{{ t('actions.savePdf') }}</span>
              </span>
            </button>
          </div>
        </details>
      </div>
    </section>

    <div v-if="partialOutcomeWarning && currentReportHasData" class="alert alert-warning" style="margin-bottom: 1.5rem;">
      <strong>{{ partialOutcomeWarning }}</strong>
      <div v-if="outcomeIssueDetails.length" style="margin-top: 0.5rem;">
        <div v-for="issue in outcomeIssueDetails" :key="issue.key">
          {{ issue.label }}
        </div>
      </div>
    </div>

    <div v-if="store.error && currentReportHasData" class="alert alert-error" style="margin-bottom: 1.5rem;">
      {{ store.error }}
    </div>

    <div class="mode-tabs no-print">
      <button
        class="mode-tab"
        :class="{ active: activeView === 'report' }"
        @click="activeView = 'report'"
      >
        {{ t('report.reportView') }}
      </button>
      <button
        v-if="hasRawReportData"
        class="mode-tab"
        :class="{ active: activeView === 'raw' }"
        @click="activeView = 'raw'"
      >
        {{ t('report.rawData') }}
      </button>
    </div>

    <section v-if="activeView === 'raw'" class="raw-card">
      <div class="chapter-head">
        <div>
          <span class="chapter-kicker">{{ t('report.rawData') }}</span>
          <h3 class="chapter-title">{{ t('report.rawData') }}</h3>
        </div>
      </div>
      <pre v-if="rawReportJson">{{ rawReportJson }}</pre>
      <p v-else>{{ t('report.rawDataUnavailable') }}</p>
    </section>

    <div v-else-if="report && !currentReportHasData" class="raw-card">
      <div class="chapter-head">
        <div>
          <span class="chapter-kicker">{{ t('report.title') }}</span>
          <h3 class="chapter-title">{{ t('report.vehicleDataUnavailable') }}</h3>
        </div>
      </div>
      <p>{{ t('report.vehicleDataUnavailableDesc') }}</p>
      <button
        class="button primary"
        style="margin-top: 1rem;"
        :disabled="isRetryingVehicle"
        @click="handleRetryVehicle"
      >
        <span v-if="isRetryingVehicle">{{ t('report.retrying') }}</span>
        <span v-else>{{ t('report.retryVehicle') }}</span>
      </button>
    </div>

    <template v-else-if="report">
      <section class="summary-band">
        <article class="metric-card" style="display:flex;align-items:center;gap:8px;">
          <div style="flex:1;min-width:0;">
            <span class="metric-label">{{ t('report.batterySoh') }}</span>
            <strong class="metric-value">{{ batterySummary?.batterySoh || t('report.notAvailable') }}</strong>
            <span class="metric-sub">{{ batterySummary?.batteryCapacityKwh || t('report.notAvailable') }}</span>
          </div>
          <div style="flex-shrink:0;width:90px;height:90px;">
            <MiniSoh v-if="sohNumber > 0" :soh="sohNumber" />
          </div>
        </article>
        <article class="metric-card" style="display:flex;align-items:center;gap:8px;">
          <div style="flex:1;min-width:0;">
            <span class="metric-label">{{ t('report.chargingSessions') }}</span>
            <strong class="metric-value">{{ chargingSummary?.sessionCount ?? 0 }}</strong>
            <span class="metric-sub">{{ invoiceCount }} {{ t('report.invoices') }}</span>
          </div>
          <div style="flex-shrink:0;width:90px;height:90px;">
            <MiniCharging v-if="miniChargingValues.length > 0" :values="miniChargingValues" />
          </div>
        </article>
        <article class="metric-card">
          <span class="metric-label">{{ t('report.warrantyCoverages') }}</span>
          <strong class="metric-value">{{ warrantySummary?.coverageCount ?? 0 }}</strong>
          <span class="metric-sub">{{ warrantySummary?.nearestExpirationDate ? formatDate(warrantySummary.nearestExpirationDate) : t('report.notAvailable') }}</span>
        </article>
        <article class="metric-card" style="display:flex;align-items:center;gap:8px;">
          <div style="flex:1;min-width:0;">
            <span class="metric-label">{{ t('report.recentAlerts') }}</span>
            <strong class="metric-value">{{ alertSummary?.totalCount ?? 0 }}</strong>
            <span class="metric-sub">{{ alertSummary?.chargingAlertCount ?? 0 }} {{ t('report.chargingRelated') }}</span>
          </div>
          <div style="flex-shrink:0;width:90px;height:90px;">
            <MiniAlerts v-if="miniAlertCategories.length > 0" :categories="miniAlertCategories" />
          </div>
        </article>
      </section>

      <section class="chapter-stack">
        <section class="chapter">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ translateChapterTitle('Vehicle') }}</span>
              <h3 class="chapter-title">{{ translateChapterTitle('Vehicle Overview') }}</h3>
            </div>
            <span class="chapter-badge">{{ translateChapterTitle('Vehicle snapshot') }}</span>
          </div>

          <div class="grid-4">
            <article class="metric-card">
              <span class="metric-label">{{ t('report.displayName') }}</span>
              <strong class="metric-value">{{ displayName }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.trim') }}</span>
              <strong class="metric-value">{{ trimLabel }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.odometer') }}</span>
              <strong class="metric-value">{{ odometerLabel }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.state') }}</span>
              <strong class="metric-value">{{ displayText(vehicleDataRecord?.state) }}</strong>
            </article>
          </div>

          <section class="subpanel full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Vehicle Identity') }}</h3>
            <div class="grid-3">
              <section v-for="panel in vehicleIdentityPanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value" :class="{ mono: row.mono }">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section class="subpanel full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Current Vehicle State') }}</h3>
            <div class="grid-3">
              <section v-for="panel in currentStatePanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section class="subpanel full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Vehicle Access') }}</h3>
            <div class="grid-3">
              <section v-for="panel in accessPanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section class="subpanel full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Doors and Windows') }}</h3>
            <div class="grid-2">
              <section v-for="panel in openingPanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section class="subpanel full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Climate and Comfort') }}</h3>
            <div class="climate-grid">
              <section v-for="panel in climatePanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <div class="grid-2" style="margin-top: 16px;">
            <section v-for="panel in mediaPanels" :key="panel.title" class="subpanel">
              <h3 class="subpanel-title">{{ panel.title }}</h3>
              <div class="rows">
                <div v-for="row in panel.rows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section class="chapter">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ translateChapterTitle('Battery') }}</span>
              <h3 class="chapter-title">{{ translateChapterTitle('Battery and Range') }}</h3>
            </div>
            <span class="chapter-badge">{{ translateChapterTitle('Battery health') }}</span>
          </div>

          <div class="grid-4">
            <article class="metric-card">
              <span class="metric-label">{{ t('report.batterySoh') }}</span>
              <strong class="metric-value">{{ batterySummary?.batterySoh || t('report.notAvailable') }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.batteryCapacity') }}</span>
              <strong class="metric-value">{{ batterySummary?.batteryCapacityKwh || t('report.notAvailable') }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.estimatedRange') }}</span>
              <strong class="metric-value">{{ estimatedRangeLabel }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.chargeLimit') }}</span>
              <strong class="metric-value">{{ chargeLimitLabel }}</strong>
            </article>
          </div>

          <div class="chart-row" style="margin-top:16px;">
            <div class="chart-panel">
              <h3 class="chart-panel-title">{{ t('report.cptAnalysis.sohPanelTitle') }}</h3>
              <span class="chart-panel-sub">{{ t('report.cptAnalysis.sohPanelSub', { soh: batterySummary?.batterySoh }) }}</span>
              <SohGauge v-if="sohNumber > 0" :soh="sohNumber" :label="t('report.cptAnalysis.sohPanelTitle')" />
            </div>
            <div class="chart-panel">
              <h3 class="chart-panel-title">{{ t('report.cptAnalysis.analysisPanelTitle') }}</h3>
              <span class="chart-panel-sub">{{ t('report.cptAnalysis.analysisPanelSub') }}</span>
              <template v-if="projection">
                <div class="finding-list">
                  <div class="finding">
                    <span class="finding-dot" :class="projection.result.degradationRatePer1000km < 0.10 ? 'good' : projection.result.degradationRatePer1000km < 0.20 ? 'warn' : 'bad'"></span>
                    <i18n-t keypath="report.cptAnalysis.findingDegradationRate" tag="span">
                      <template #rate><strong>{{ projection.result.degradationRatePer1000km }}</strong></template>
                      <template #est><strong>{{ projection.result.estimatedSoh }}</strong></template>
                      <template #meas><strong>{{ batterySummary?.batterySoh }}</strong></template>
                    </i18n-t>
                  </div>
                  <div class="finding">
                    <span class="finding-dot" :class="projection.result.projectedKmToWarrantyFloor > 200000 ? 'good' : projection.result.projectedKmToWarrantyFloor > 150000 ? 'warn' : 'bad'"></span>
                    <i18n-t keypath="report.cptAnalysis.findingWarrantyFloor" tag="span">
                      <template #km><strong>{{ projection.result.projectedKmToWarrantyFloor.toLocaleString() }}</strong></template>
                    </i18n-t>
                  </div>
                  <div class="finding">
                    <span class="finding-dot" :class="projection.result.rangeReductionKm < 30 ? 'good' : projection.result.rangeReductionKm < 60 ? 'warn' : 'bad'"></span>
                    <i18n-t keypath="report.cptAnalysis.findingRatedRange" tag="span">
                      <template #range><strong>{{ estimatedRangeLabel }}</strong></template>
                      <template #reduction><strong>{{ projection.result.rangeReductionKm }}</strong></template>
                      <template #rated><strong>{{ projection.result.ratedRangeKm }}</strong></template>
                    </i18n-t>
                  </div>
                  <div class="finding">
                    <span class="finding-dot" :class="projection.result.remainingUsefulLifeYears > 10 ? 'good' : projection.result.remainingUsefulLifeYears > 5 ? 'warn' : 'bad'"></span>
                    <i18n-t keypath="report.cptAnalysis.findingRemainingLife" tag="span">
                      <template #years><strong>{{ projection.result.remainingUsefulLifeYears }}</strong></template>
                    </i18n-t>
                  </div>
                </div>
                <div class="methodology-note">
                  <strong>{{ t('report.cptAnalysis.methodologyLabel') }}</strong>
                  Age: {{ projection.result.ageYears }} yr &middot;
                  EFC: {{ projection.result.efc }} &middot;
                  f<sub>hot</sub>: {{ projection.result.stressFactors.f_hot }} ({{ projection.result.stressFactors.f_hot_source }}) &middot;
                  f<sub>highsoc</sub>: {{ projection.result.stressFactors.f_highsoc }} ({{ projection.result.stressFactors.f_highsoc_source }}) &middot;
                  <span v-if="projection.sources.dcFractionSource === 'unknown'">{{ t('report.cptAnalysis.dcNotDeterminable', { n: projection.sources.dcFractionSessionCount }) }}</span>
                  <span v-else>{{ t('report.cptAnalysis.dcDerived', { value: projection.result.stressFactors.f_dc.toFixed(2), n: projection.sources.dcFractionSessionCount }) }}</span>.
                  {{ t('report.cptAnalysis.notOfficialDiagnostic') }}
                </div>
              </template>
              <p v-else style="margin-top:12px;font-size:13px;color:var(--muted);">{{ t('report.cptAnalysis.insufficientData') }}</p>
            </div>
          </div>

          <div v-if="projection && odometerKm > 0" class="chart-panel" style="margin-top:16px;width:100%;box-sizing:border-box;overflow:hidden;">
            <h3 class="chart-panel-title">{{ t('report.cptAnalysis.trajectoryPanelTitle') }}</h3>
            <span class="chart-panel-sub">{{ t('report.cptAnalysis.trajectoryPanelSub', { km: projection.result.projectedKmToWarrantyFloor.toLocaleString() }) }}</span>
            <DegradationChart
              :odometerKm="odometerKm"
              :measuredSoh="sohNumber"
              :projectedKm="projection.result.projectedKmToWarrantyFloor"
              :labelTrajectory="t('report.cptAnalysis.legendTrajectory')"
              :labelThisVehicle="t('report.cptAnalysis.legendThisVehicle')"
              :labelWarrantyFloor="t('report.cptAnalysis.legendWarrantyFloor')"
            />
          </div>

          <div class="grid-2" style="margin-top: 16px;">
            <section v-for="panel in batteryPanels" :key="panel.title" class="subpanel">
              <h3 class="subpanel-title">{{ panel.title }}</h3>
              <div class="rows">
                <div v-for="row in panel.rows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section class="chapter">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ translateChapterTitle('Charging') }}</span>
              <h3 class="chapter-title">{{ translateChapterTitle('Charging Activity') }}</h3>
            </div>
            <span class="chapter-badge">{{ translateChapterTitle('History and use') }}</span>
          </div>

          <div class="grid-4">
            <article class="metric-card">
              <span class="metric-label">{{ t('report.sessionCount') }}</span>
              <strong class="metric-value">{{ chargingSummary?.sessionCount ?? 0 }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.invoiceFiles') }}</span>
              <strong class="metric-value">{{ invoiceCount }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.totalEnergy') }}</span>
              <strong class="metric-value">{{ totalEnergyLabel }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.latestSession') }}</span>
              <strong class="metric-value">{{ chargingSummary?.latestSessionDate ? formatDate(chargingSummary.latestSessionDate) : t('report.notAvailable') }}</strong>
            </article>
          </div>

          <div class="chart-row" style="margin-top:16px;">
            <div class="chart-panel">
              <h3 class="chart-panel-title">Charging Sessions</h3>
              <span class="chart-panel-sub">Energy per session (kWh)</span>
              <ChargingBars v-if="chargingBarSessions.length > 0" :sessions="chargingBarSessions" />
            </div>
            <div class="chart-panel">
              <h3 class="chart-panel-title">Charge Levels</h3>
              <span class="chart-panel-sub">Current charge vs limit vs rated range capacity</span>
              <BatteryBars
                v-if="batterySummary?.batteryLevel != null && batterySummary?.chargeLimitSoc != null"
                :batteryLevel="batterySummary.batteryLevel"
                :chargeLimitSoc="batterySummary.chargeLimitSoc"
              />
            </div>
          </div>

          <section class="subpanel full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Current Charge Session') }}</h3>
            <div class="grid-3">
              <section v-for="panel in chargingStatePanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section class="subpanel full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Scheduling') }}</h3>
            <div class="grid-3">
              <section v-for="panel in chargingSchedulePanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section class="subpanel soft full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Charging History') }}</h3>
            <div class="grid-4">
              <article class="metric-card">
                <span class="metric-label">{{ t('report.sessionCount') }}</span>
                <strong class="metric-value">{{ chargingSummary?.sessionCount ?? 0 }}</strong>
              </article>
              <article class="metric-card">
                <span class="metric-label">{{ translatePanelTitle('Records Shown') }}</span>
                <strong class="metric-value">{{ chargingSessions.length }}</strong>
              </article>
              <article class="metric-card">
                <span class="metric-label">{{ t('report.invoiceFiles') }}</span>
                <strong class="metric-value">{{ invoiceCount }}</strong>
              </article>
              <article class="metric-card">
                <span class="metric-label">{{ t('report.latestSession') }}</span>
                <strong class="metric-value">{{ chargingSummary?.latestSessionDate ? formatDate(chargingSummary.latestSessionDate) : t('report.notAvailable') }}</strong>
              </article>
            </div>
            <div class="history-list">
              <article v-for="session in chargingSessionsPaged" :key="`${session.sessionId}-${session.chargeStartDateTime}`" class="history-row">
                <div class="history-date">
                  {{ formatDate(session.chargeStartDateTime) }}
                  <span class="history-meta">{{ formatTimeRange(session.chargeStartDateTime, session.chargeStopDateTime) }}</span>
                </div>
                <div class="history-site">
                  {{ displayText(session.siteLocationName) }}
                  <span class="history-meta">Session {{ displayText(session.sessionId) }}</span>
                </div>
                <div class="history-energy">{{ sessionEnergyLabel(session) }}</div>
                <div class="history-cost">{{ sessionCostLabel(session) }}</div>
                <div class="history-invoice">
                  <template v-if="session.invoices?.length">
                    <a
                      v-for="invoice in session.invoices"
                      :key="invoice.contentId"
                      :href="invoiceDownloadUrl(invoice.contentId)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="report-link"
                    >
                      {{ invoice.fileName || t('report.downloadInvoicePdf') }}
                    </a>
                  </template>
                  <template v-else>{{ t('report.notAvailable') }}</template>
                </div>
              </article>
            </div>
            <div v-if="chargingSessions.length > 25" class="history-pagination no-print">
              <div class="pagination-controls">
                <button class="pagination-btn" :disabled="chargingCurrentPage === 1" @click="chargingCurrentPage--">&#8249;</button>
                <span class="pagination-info">{{ chargingCurrentPage }} / {{ chargingTotalPages }}</span>
                <button class="pagination-btn" :disabled="chargingCurrentPage >= chargingTotalPages" @click="chargingCurrentPage++">&#8250;</button>
              </div>
              <select class="pagination-size" :value="chargingPageSize" @change="e => { chargingPageSize = Number((e.target as HTMLSelectElement).value) as 25 | 100; chargingCurrentPage = 1; }">
                <option value="25">25 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </section>
        </section>

        <section class="chapter">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ translateChapterTitle('Warranty') }}</span>
              <h3 class="chapter-title">{{ translateChapterTitle('Warranty and Service History') }}</h3>
            </div>
            <span class="chapter-badge">{{ translateChapterTitle('Protection') }}</span>
          </div>

          <div class="grid-3">
            <article class="metric-card">
              <span class="metric-label">{{ t('report.activeCoverages') }}</span>
              <strong class="metric-value">{{ warrantySummary?.coverageCount ?? 0 }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.nearestExpiry') }}</span>
              <strong class="metric-value">{{ warrantySummary?.nearestExpirationDate ? formatDate(warrantySummary.nearestExpirationDate) : t('report.notAvailable') }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.serviceStatus') }}</span>
              <strong class="metric-value">{{ serviceStatusLabel }}</strong>
            </article>
          </div>

          <div class="grid-2" style="margin-top: 16px;">
            <section v-for="panel in warrantyPanels" :key="panel.title" class="subpanel">
              <h3 class="subpanel-title">{{ panel.title }}</h3>
              <div class="rows">
                <div v-for="row in panel.rows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section class="chapter">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ translateChapterTitle('Alerts') }}</span>
              <h3 class="chapter-title">{{ translateChapterTitle('Recent Alerts') }}</h3>
            </div>
            <span class="chapter-badge">{{ translateChapterTitle('Recent vehicle alerts') }}</span>
          </div>

          <div class="alert-summary">
            <article class="metric-card">
              <span class="metric-label">{{ t('report.alertCount') }}</span>
              <strong class="metric-value">{{ alertSummary?.totalCount ?? 0 }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.chargingAlerts') }}</span>
              <strong class="metric-value">{{ alertSummary?.chargingAlertCount ?? 0 }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.repeatedIssues') }}</span>
              <strong class="metric-value">{{ alertSummary?.repeatedAlerts?.length ?? 0 }}</strong>
            </article>
            <article class="metric-card">
              <span class="metric-label">{{ t('report.categories') }}</span>
              <strong class="metric-value">{{ alertSummary?.categories?.length ?? 0 }}</strong>
            </article>
          </div>

          <div class="chip-row">
            <span v-for="category in alertSummary?.categories || []" :key="category.label" class="chip">
              {{ t(`apiValues.alertCategory.${category.label}`) !== `apiValues.alertCategory.${category.label}` ? t(`apiValues.alertCategory.${category.label}`) : category.label }} ({{ category.count }})
            </span>
          </div>

          <div class="alert-list">
            <article v-for="alert in recentAlerts" :key="`${alert.name}-${alert.time}`" class="alert-row">
              <div class="alert-date">{{ formatDate(alert.time) }}</div>
              <div class="alert-text">{{ displayText(alert.user_text) }}</div>
              <div class="alert-meta">
                <span class="chip">{{ alertCategoryLabel(alert.name) }}</span>
                <span class="alert-code">{{ alert.name }}</span>
              </div>
            </article>
          </div>
        </section>

        <section class="chapter">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ translateChapterTitle('Specification') }}</span>
              <h3 class="chapter-title">{{ translateChapterTitle('Features and Specification') }}</h3>
            </div>
            <span class="chapter-badge">{{ translateChapterTitle('Equipment and build') }}</span>
          </div>

          <section class="subpanel full-width">
            <h3 class="subpanel-title">{{ translatePanelTitle('Exterior and Hardware') }}</h3>
            <div class="grid-3">
              <section v-for="panel in specificationPanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section class="subpanel full-width" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Cabin and Preferences') }}</h3>
            <div class="grid-3">
              <section v-for="panel in cabinPanels" :key="panel.title" class="subpanel">
                <h3 class="subpanel-title">{{ panel.title }}</h3>
                <div class="rows">
                  <div v-for="row in panel.rows" :key="row.label" class="row">
                    <span class="label">{{ row.label }}</span>
                    <span class="value">{{ row.value }}</span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <div v-if="specCells.length" class="subpanel" style="margin-top: 16px;">
            <h3 class="subpanel-title">{{ translatePanelTitle('Vehicle Specification') }}</h3>
            <div class="spec-grid">
              <div v-for="cell in specCells" :key="cell.label" class="spec-cell">
                <span>{{ cell.label }}</span>
                <strong>{{ cell.value }}</strong>
              </div>
            </div>
          </div>

          <div class="grid-2" style="margin-top: 16px;">
            <section v-if="optionCodeRows.length" class="subpanel">
              <h3 class="subpanel-title">{{ translatePanelTitle('Option Codes') }}</h3>
              <div class="rows">
                <div v-for="row in optionCodeRows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
            <section v-if="equipmentPriceRows.length" class="subpanel">
              <h3 class="subpanel-title">{{ translatePanelTitle('Equipment Price Breakdown') }}</h3>
              <div class="rows">
                <div v-for="row in equipmentPriceRows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section class="chapter">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ translateChapterTitle('Software') }}</span>
              <h3 class="chapter-title">{{ translateChapterTitle('Software and Updates') }}</h3>
            </div>
            <span class="chapter-badge">{{ translateChapterTitle('Current software') }}</span>
          </div>

          <div class="grid-2" style="margin-top: 16px;">
            <section class="subpanel">
              <h3 class="subpanel-title">{{ translatePanelTitle('Software Status') }}</h3>
              <div class="rows">
                <div v-for="row in softwareStatusRows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
            <section class="subpanel">
              <h3 class="subpanel-title">{{ translatePanelTitle('Release Notes') }}</h3>
              <div class="rows">
                <div v-for="row in releaseVersionRows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
          </div>

          <div class="grid-2" style="margin-top: 16px;">
            <section class="subpanel">
              <h3 class="subpanel-title">{{ translatePanelTitle('Release Notes Metadata') }}</h3>
              <div class="rows">
                <div v-for="row in releaseMetadataRows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
            <section class="subpanel">
              <h3 class="subpanel-title">{{ translatePanelTitle('Release Note Detail Fields') }}</h3>
              <div class="rows">
                <div v-for="row in releaseDetailRows" :key="row.label" class="row">
                  <span class="label">{{ row.label }}</span>
                  <span class="value">{{ row.value }}</span>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section class="chapter">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ translateChapterTitle('Photos') }}</span>
              <h3 class="chapter-title">{{ t('report.vehiclePhotos') }}</h3>
            </div>
            <span class="chapter-badge">{{ currentPhotos.length }} / 10</span>
          </div>
          <div class="photo-grid">
            <template v-if="currentPhotos.length">
              <article v-for="(photo, idx) in currentPhotos" :key="idx" class="photo-report-card">
                <img :src="photo" class="photo-report-img" :alt="`Vehicle photo ${idx + 1}`" />
              </article>
            </template>
            <template v-else>
              <article v-for="slot in 4" :key="slot" class="photo-card">Photo slot {{ slot }}</article>
            </template>
          </div>
        </section>

        <section v-if="currentPhotos.length" class="photo-preview-section no-print">
          <div class="photo-preview-grid">
            <div v-for="(photo, idx) in currentPhotos" :key="idx" class="photo-preview-card">
              <img :src="photo" class="photo-preview-img" :alt="`Photo ${idx + 1}`" />
              <button class="photo-remove-btn" @click="handleRemovePhoto(idx)" :title="t('common.remove')">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <line x1="4" y1="4" x2="12" y2="12" />
                  <line x1="12" y1="4" x2="4" y2="12" />
                </svg>
              </button>
            </div>
          </div>
        </section>

        <section class="glossary-card">
          <div class="chapter-head">
            <div>
              <span class="chapter-kicker">{{ t('glossary.sectionTitle') }}</span>
              <h3 class="chapter-title">{{ t('glossary.sectionTitle') }}</h3>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>{{ t('apiValues.glossaryTable.Term') }}</th>
                <th>{{ t('apiValues.glossaryTable.Meaning') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in glossaryItems" :key="item.term">
                <td>{{ item.term }}</td>
                <td>{{ item.definition }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </section>
    </template>

    <div v-else class="raw-card no-print">
      <div class="chapter-head">
        <div>
          <span class="chapter-kicker">{{ t('report.title') }}</span>
          <h3 class="chapter-title">{{ t('report.noReportDataAvailable') }}</h3>
        </div>
      </div>
      <p>{{ t('report.noReportDataAvailable') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';
import { trackEvent } from '@/utils/analytics';
import {
  deleteReport,
  downloadReceiptPdf,
  generatePdfReport,
  generatePdfReportAsync,
  getChargingCsvUrl,
  getChargingInvoiceDownloadUrl,
  getReportTaskStatus,
  getSessionInvoiceZipUrl,
} from '@/services/api';
import { buildPdfZipSummary, useSessionPdfZip } from '@/composables/useSessionPdfZip';
import type { ChargingSession, ChargingSessionFee, FormattedField, OptionCode, ReleaseNote } from '@/types';
import { SohGauge, BatteryBars, ChargingBars, MiniSoh, MiniCharging, MiniAlerts } from '@/components/charts';
import DegradationChart from '@/components/charts/DegradationChart.vue';
import { useBatteryProjection } from '@/composables/useBatteryProjection';
import { themeLight, toggleTheme } from '@/composables/useReportTheme';

type InfoRow = {
  label: string;
  value: string;
  mono?: boolean;
};

type InfoPanel = {
  title: string;
  rows: InfoRow[];
};

type SpecCell = {
  label: string;
  value: string;
};

const { t, locale } = useI18n();
const store = useAppStore();
const {
  isRunning: isZipRunning,
  progress: zipProgress,
  error: zipError,
  downloadAllPdfs,
} = useSessionPdfZip();

const reportVins = computed(() => Object.keys(store.vehicleReports));
const report = computed(() => {
  if (store.activeReportVin && store.vehicleReports[store.activeReportVin]) {
    return store.vehicleReports[store.activeReportVin];
  }
  return store.vehicleReport;
});

// --- formattedFields helpers (API-driven panels) ---
const fieldsByPanel = computed<Record<string, FormattedField[]>>(() => {
  const fields = report.value?.formattedFields;
  if (!fields?.length) return {};
  const map: Record<string, FormattedField[]> = {};
  for (const f of fields) {
    if (!map[f.panel]) map[f.panel] = [];
    map[f.panel].push(f);
  }
  return map;
});

function ffPanel(panelName: string, title: string): InfoPanel {
  const fields = fieldsByPanel.value[panelName] || [];
  return {
    title: translatePanelTitle(title),
    rows: fields.map(f => ({ label: translateLabel(f.label), value: f.value ? translateApiValue(f.value) : notAvailable(), mono: f.mono })),
  };
}

function ffRows(panelName: string): InfoRow[] {
  const fields = fieldsByPanel.value[panelName] || [];
  return fields.map(f => ({ label: translateLabel(f.label), value: f.value ? translateApiValue(f.value) : notAvailable(), mono: f.mono }));
}

const reportOutcome = computed(() => {
  if (store.activeReportVin && store.vehicleReportOutcomes[store.activeReportVin]) {
    return store.vehicleReportOutcomes[store.activeReportVin];
  }
  const firstOutcome = Object.values(store.vehicleReportOutcomes)[0];
  return firstOutcome || null;
});

const outcomeIssueDetails = computed(() => {
  const outcome = reportOutcome.value;
  if (!outcome) return [];
  return Object.entries(outcome.modules)
    .filter(([moduleName, value]) => (
      value.status === 'empty'
      && value.error_detail
      && moduleName !== 'vehicle_state'
      && value.error_detail !== 'drive_state_missing'
    ))
    .map(([moduleName, value]) => ({
      key: `${moduleName}-${value.error_detail}`,
      label: `${moduleName}: ${value.error_detail}`,
    }));
});

const partialOutcomeWarning = computed(() => {
  const outcome = reportOutcome.value;
  if (!outcome || outcome.core_status !== 'partial') return null;
  if (outcomeIssueDetails.value.length > 0) {
    return t('report.partialDataReturned');
  }
  return null;
});

const reportVin = computed(() => report.value?.vehicle?.vin || report.value?.vehicleData?.vin || null);
const reportLead = computed(() => t('report.heroDescription'));

const packageHistoryMonths = computed(() => {
  const selected = store.selectedPlan;
  const fromCatalog = store.catalog?.packages?.find(pkg => pkg.name === selected)?.history_months;
  return fromCatalog || 24;
});

const invoiceCount = computed(() => {
  return (report.value?.chargingHistory || []).reduce((sum, session) => sum + (session.invoices?.length || 0), 0);
});

const chargingCsvUrl = computed(() => {
  if (!store.sessionId || !reportVin.value) {
    return null;
  }
  return getChargingCsvUrl(store.sessionId, reportVin.value, packageHistoryMonths.value);
});

const sessionInvoiceZipUrl = computed(() => {
  if (!store.sessionId || invoiceCount.value === 0) {
    return null;
  }
  return getSessionInvoiceZipUrl(store.sessionId, packageHistoryMonths.value);
});

const sessionReportCount = computed(() => Object.keys(store.vehicleReports || {}).length || (report.value ? 1 : 0));
const sessionInvoiceTotal = computed(() => {
  const reports = Object.values(store.vehicleReports || {}) as any[];
  if (reports.length === 0) return invoiceCount.value;
  return reports.reduce((sum: number, r: any) => {
    const hist = r?.chargingHistory || [];
    return sum + hist.reduce((s: number, sess: any) => s + (sess.invoices?.length || 0), 0);
  }, 0);
});
const pdfZipSummary = computed(() => buildPdfZipSummary({
  reportCount: sessionReportCount.value,
  invoiceCount: sessionInvoiceTotal.value,
  hasReceipt: Boolean(store.paymentId),
  months: packageHistoryMonths.value,
}));

const handleDownloadAllPdfs = async () => {
  if (!store.sessionId || !pdfZipSummary.value.canDownload) return;
  trackEvent('file_download', { file_type: 'session_pdf_zip' });
  // Photos are browser-only; send per-VIN so ZIP report PDFs match single Save PDF.
  const vehiclePhotos: Record<string, string[]> = {};
  for (const vin of reportVins.value) {
    vehiclePhotos[vin] = store.getVehiclePhotos(vin);
  }
  const ok = await downloadAllPdfs({
    sessionId: store.sessionId,
    months: packageHistoryMonths.value,
    lang: String(locale.value),
    paymentId: store.paymentId,
    include: { reports: true, invoices: true, receipt: Boolean(store.paymentId) },
    vehiclePhotos,
  });
  if (!ok && zipError.value) {
    store.setError(zipError.value);
  }
};


const isGeneratingPdf = ref(false);
const pdfProgress = ref(0);
const activeView = ref<'report' | 'raw'>('report');
const isRetryingVehicle = ref(false);
const isDownloadingReceipt = ref(false);
const receiptDownloaded = ref(false);

const handleRetryVehicle = async () => {
  const sessionId = store.sessionId;
  if (!sessionId) return;
  isRetryingVehicle.value = true;
  store.setError(null);
  try {
    // Merge the freshly generated report(s) into the existing set via the store.
    await store.generateVehicleReports(locale.value, { merge: true });
  } catch (error) {
    store.setError(error instanceof Error ? error.message : 'Retry failed');
  } finally {
    isRetryingVehicle.value = false;
  }
};

const currentPhotos = computed(() => {
  if (!reportVin.value) return [];
  return store.getVehiclePhotos(reportVin.value);
});

const nowMs = ref(Date.now());
const expiryIntervalId = ref<number | null>(null);

const vehicleDataRecord = computed<Record<string, any>>(() => report.value?.vehicleData || {});
const chargeState = computed<Record<string, any>>(() => vehicleDataRecord.value?.charge_state || {});
const vehicleConfig = computed<Record<string, any>>(() => vehicleDataRecord.value?.vehicle_config || {});
const vehicleState = computed<Record<string, any>>(() => vehicleDataRecord.value?.vehicle_state || {});
const releaseNotesEnvelope = computed<Record<string, any>>(() => report.value?.releaseNotes || {});
const vehicleSpecsSource = computed<Record<string, any>>(() => {
  const source = (report.value as any)?.vehicleSpecs?.response || (report.value as any)?.vehicleSpecs || {};
  return typeof source === 'object' && source !== null ? source as Record<string, any> : {};
});
const chargingSessions = computed<ChargingSession[]>(() => report.value?.chargingHistory || []);
const chargingPageSize = ref<25 | 100>(25);
const chargingCurrentPage = ref(1);
const chargingTotalPages = computed(() => Math.max(1, Math.ceil(chargingSessions.value.length / chargingPageSize.value)));
const chargingSessionsPaged = computed(() => {
  const start = (chargingCurrentPage.value - 1) * chargingPageSize.value;
  return chargingSessions.value.slice(start, start + chargingPageSize.value);
});
const recentAlerts = computed<any[]>(() => report.value?.recentAlerts?.recent_alerts || []);

const activeOptions = computed<OptionCode[]>(() => {
  const optionsSource = (report.value as any)?.options;
  const optionsList = Array.isArray(optionsSource)
    ? optionsSource
    : Array.isArray(optionsSource?.codes)
      ? optionsSource.codes
      : [];
  return optionsList.filter((option: OptionCode) => option.isActive !== false);
});

const releaseNotes = computed<ReleaseNote[]>(() => {
  return releaseNotesEnvelope.value?.release_notes || [];
});

const chargingSummary = computed<any>(() => report.value?.chargingSummary || null);
const batterySummary = computed<any>(() => report.value?.batterySummary || null);
const sohNumber = computed(() => parseFloat(batterySummary.value?.batterySoh ?? '') || 0);
const { projection } = useBatteryProjection(() => report.value);
const miniChargingValues = computed(() =>
  chargingSessions.value.slice(-20).map(s => s.fees.find(f => f.feeType === 'CHARGING')?.usageBase ?? 0)
);
const miniAlertCategories = computed(() =>
  (alertSummary.value?.categories ?? []).map((c: { label: string; count: number }) => ({ name: c.label, count: c.count }))
);
const chargingBarSessions = computed(() =>
  chargingSessions.value.slice(-50).map(s => ({
    label: formatDate(s.chargeStartDateTime),
    kwh: s.fees.find(f => f.feeType === 'CHARGING')?.usageBase ?? 0,
  }))
);
const alertSummary = computed<any>(() => report.value?.alertSummary || null);
const warrantySummary = computed<any>(() => report.value?.warrantySummary || null);

const warrantyEntries = computed<any[]>(() => {
  return report.value?.warrantyDetails?.activeWarranty || [];
});

const hasRawReportData = computed(() => {
  return !!report.value?.rawReportData && Object.keys(report.value.rawReportData).length > 0;
});

const rawReportJson = computed(() => {
  if (!report.value?.rawReportData) return '';
  return JSON.stringify(report.value.rawReportData, null, 2);
});

const sessionExpiryLabel = computed(() => {
  const expiresAt = store.sessionExpiresAt;
  if (!expiresAt) return null;
  const expiresMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return null;
  const diff = Math.max(0, expiresMs - nowMs.value);
  const totalSeconds = Math.floor(diff / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

const notAvailable = (): string => t('report.notAvailable');

const translateApiValue = (value: string): string => {
  const apiSections = [
    'apiValues.vehicleState',
    'apiValues.chargingState',
    'apiValues.dashcamState',
    'apiValues.climateKeeperMode',
    'apiValues.cabinOverheatProtection',
    'apiValues.scheduledChargingMode',
    'apiValues.softwareUpdateStatus',
    'apiValues.chargePortLatch',
    'apiValues.boolean',
    'apiValues.preconditioningTimes',
    'apiValues.offPeakChargingTimes',
    'apiValues.carType',
    'apiValues.exteriorColor',
    'apiValues.wheelType',
    'apiValues.interiorTrimType',
    'apiValues.driverAssist',
    'apiValues.roofColor',
    'apiValues.rearDriveUnit',
    'apiValues.performancePackage',
    'apiValues.spoilerType',
    'apiValues.chargePortType',
    'apiValues.exteriorTrim',
    'apiValues.headlampType',
    'apiValues.carSpecialType',
    'apiValues.thirdRowSeats',
    'apiValues.auxParkLamps',
    'apiValues.trimBadging',
  ];
  for (const section of apiSections) {
    const key = `${section}.${value}`;
    if (t(key) !== key) return t(key);
  }
  return value;
};

const translateLabel = (label: string): string => {
  const key = `apiValues.fieldLabel.${label}`;
  const result = t(key);
  return result !== key ? result : label;
};

const translatePanelTitle = (title: string): string => {
  const key = `apiValues.panelTitle.${title}`;
  const result = t(key);
  return result !== key ? result : title;
};

const translateChapterTitle = (title: string): string => {
  const key = `apiValues.chapterTitle.${title}`;
  const result = t(key);
  return result !== key ? result : title;
};

const displayText = (value: unknown): string => {
  if (value === null || value === undefined || value === '' || value === '<invalid>') {
    return notAvailable();
  }
  return translateApiValue(String(value));
};

const displayBool = (value: unknown, yesLabel = t('report.yes'), noLabel = t('report.no')): string => {
  if (value === null || value === undefined) return notAvailable();
  return Boolean(value) ? yesLabel : noLabel;
};

const displayState = (value: unknown, activeLabel = t('report.active'), inactiveLabel = t('report.inactive')): string => {
  if (value === null || value === undefined) return notAvailable();
  return Boolean(value) ? activeLabel : inactiveLabel;
};

const formatDate = (value?: string | null): string => {
  if (!value) return notAvailable();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString(locale.value);
};

const formatTimeRange = (start?: string | null, end?: string | null): string => {
  if (!start) return notAvailable();
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;
  if (Number.isNaN(startDate.getTime())) return displayText(start);
  const startLabel = startDate.toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' });
  if (!endDate || Number.isNaN(endDate.getTime())) return startLabel;
  const endLabel = endDate.toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' });
  return `${startLabel}-${endLabel}`;
};

const formatMilesAsKm = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return notAvailable();
  return `${(value * 1.60934).toFixed(1)} km`;
};

const formatPercent = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === '') return notAvailable();
  return String(value).includes('%') ? String(value) : `${value}%`;
};

const formatPrice = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return notAvailable();
  return new Intl.NumberFormat(locale.value).format(value);
};

const makeRow = (label: string, value: string, mono = false): InfoRow => ({ label, value, mono });

const trimLabel = computed(() => displayText(vehicleConfig.value?.trim_badging || vehicleSpecsSource.value?.trim));
const odometerLabel = computed(() => formatMilesAsKm(vehicleState.value?.odometer));
const odometerKm = computed(() => (vehicleState.value?.odometer ?? 0) * 1.60934);
const estimatedRangeLabel = computed(() => {
  if (typeof batterySummary.value?.estimatedRangeKm === 'number') {
    return `${batterySummary.value.estimatedRangeKm.toFixed(1)} km`;
  }
  return formatMilesAsKm(chargeState.value?.est_battery_range);
});
const chargeLimitLabel = computed(() => formatPercent(chargeState.value?.charge_limit_soc ?? batterySummary.value?.chargeLimitSoc));
const totalEnergyLabel = computed(() => {
  if (typeof chargingSummary.value?.totalEnergyKwh === 'number') {
    return `${chargingSummary.value.totalEnergyKwh.toFixed(1)} kWh`;
  }
  return '0.0 kWh';
});
const serviceStatusLabel = computed(() => {
  return displayText(report.value?.serviceData?.service_status || (vehicleDataRecord.value?.in_service ? t('report.inService') : t('report.notInService')));
});

const displayName = computed(() => {
  return (
    vehicleDataRecord.value?.display_name
    || vehicleState.value?.vehicle_name
    || [report.value?.vehicle?.year, report.value?.vehicle?.make, report.value?.vehicle?.model].filter(Boolean).join(' ')
    || notAvailable()
  );
});

const vehicleIdentityPanels = computed<InfoPanel[]>(() => [
  ffPanel('model', 'Model'),
  ffPanel('registration', 'Registration'),
  ffPanel('ids', 'IDs'),
]);

const currentStatePanels = computed<InfoPanel[]>(() => [
  ffPanel('security', 'Security and Presence'),
  ffPanel('controls', 'Controls and Driving'),
  ffPanel('diagnostics', 'Diagnostics and Limits'),
]);

const accessPanels = computed<InfoPanel[]>(() => [
  ffPanel('access_service', 'Access and Service'),
  ffPanel('connectivity', 'Charging and Connectivity'),
  ffPanel('camera_tokens', 'Camera and Tokens'),
]);

const openingPanels = computed<InfoPanel[]>(() => [
  ffPanel('doors_trunks', 'Doors and Trunks'),
  ffPanel('windows', 'Windows'),
]);

const climatePanels = computed<InfoPanel[]>(() => [
  ffPanel('temperature', 'Temperature'),
  ffPanel('systems', 'Protection'),
  ffPanel('seats', 'Seat and Wheel Heating'),
]);

const mediaPanels = computed<InfoPanel[]>(() => [
  ffPanel('playback', 'Media Activity'),
  ffPanel('playback_detail', 'Playback Detail'),
]);

const batteryPanels = computed<InfoPanel[]>(() => {
  const healthRows = ffRows('health');
  const rangeRows = ffRows('range');
  return [
    { title: translatePanelTitle('Battery Status'), rows: [...healthRows, ...rangeRows] },
    ffPanel('condition', 'Battery Summary'),
  ];
});

const chargingStatePanels = computed<InfoPanel[]>(() => [
  ffPanel('current_session', 'Session State'),
  ffPanel('electrical', 'Electrical Detail'),
  ffPanel('hardware', 'Port and Flags'),
]);

const chargingSchedulePanels = computed<InfoPanel[]>(() => [
  ffPanel('schedule', 'Scheduling'),
]);

const displayWarrantyEntries = computed(() => {
  const summaryCoverages = warrantySummary.value?.coverages || [];
  if (summaryCoverages.length) {
    return summaryCoverages.map((entry: any, idx: number) => ({
      key: `${entry.warrantyType || 'warranty'}-${idx}`,
      label: entry.label || entry.warrantyType || t('report.warrantyInfo'),
      expirationDate: entry.expirationDate || null,
      expirationOdometer: entry.expirationOdometer ?? null,
      odometerUnit: entry.odometerUnit || 'km',
    }));
  }
  return warrantyEntries.value.map((entry: any, idx: number) => ({
    key: `${entry.warrantyType || 'warranty'}-${idx}`,
    label: entry.warrantyDisplayName || entry.warrantyType || t('report.warrantyInfo'),
    expirationDate: entry.expirationDate || null,
    expirationOdometer: entry.expirationOdometer ?? null,
    odometerUnit: entry.odometerUnit || 'km',
  }));
});

const warrantyPanels = computed<InfoPanel[]>(() => [
  {
    title: translatePanelTitle('Coverage Details'),
    rows: [
      ...displayWarrantyEntries.value.map((entry: any) => makeRow(translateLabel(entry.label), `${entry.expirationDate ? `${formatDate(entry.expirationDate)}` : notAvailable()}${entry.expirationOdometer ? ` / ${entry.expirationOdometer} ${entry.odometerUnit || 'km'}` : ''}`)),
      makeRow(translateLabel('Warranty Type'), displayText(warrantySummary.value?.warrantyType)),
      makeRow(translateLabel('Coverage Age'), displayText(warrantySummary.value?.coverageAge)),
      makeRow(translateLabel('Odometer Unit'), displayText(warrantySummary.value?.odometerUnit)),
      makeRow(translateLabel('Expired Coverage Date'), displayText(warrantySummary.value?.expiredCoverageDate)),
    ],
  },
  {
    title: translatePanelTitle('Service History'),
    rows: [
      makeRow(translateLabel('Current Service Status'), serviceStatusLabel.value),
      makeRow(
        translateLabel('Service Records'),
        displayText(
          ((report.value as any)?.serviceData?.service_records_summary)
          || ((report.value?.serviceData?.appointments?.length || report.value?.serviceData?.messages?.length)
            ? `${report.value?.serviceData?.appointments?.length || 0} appointments, ${report.value?.serviceData?.messages?.length || 0} messages`
            : translateLabel('No service records in current report data')),
        ),
      ),
      makeRow(translateLabel('Service Mode'), displayState(vehicleState.value?.service_mode)),
    ],
  },
]);

const alertCategoryLabel = (name?: string | null): string => {
  const raw = String(name || '');
  if (raw.startsWith('CP_') || raw.startsWith('PCS_') || raw.startsWith('UMC_')) return t('apiValues.alertCategory.Charging');
  if (raw.includes('TPMS')) return t('apiValues.alertCategory.Tires');
  if (raw.includes('ISA') || raw.includes('DDAW')) return t('apiValues.alertCategory.Driver Assist');
  return t('apiValues.alertCategory.Other');
};

const specificationPanels = computed<InfoPanel[]>(() => [
  ffPanel('exterior', 'Exterior Finish'),
  ffPanel('hardware_drive', 'Hardware and Drive'),
  ffPanel('capabilities', 'Capabilities'),
]);

const cabinPanels = computed<InfoPanel[]>(() => [
  ffPanel('cabin', 'Cabin Setup'),
  ffPanel('settings', 'Display and Units'),
  ffPanel('region_platform', 'Region and Platform'),
]);

const specCells = computed<SpecCell[]>(() => {
  const spec = vehicleSpecsSource.value;
  const cells: SpecCell[] = [
    { label: translateLabel('Trim'), value: displayText(spec.trim) },
    { label: translateLabel('Type Of Drive'), value: displayText(spec.typeOfDrive) },
    { label: translateLabel('Autopilot Software Code'), value: displayText(spec.autopilotSoftwareCode) },
    { label: translateLabel('Autopilot Hardware Code'), value: displayText(spec.autopilotHardwareCode) },
    { label: translateLabel('Battery Capacity'), value: displayText(spec.batteryCapacityKwh) },
    { label: translateLabel('Battery Range'), value: displayText(spec.batteryRangeInKm) },
    { label: translateLabel('Battery SoH Timestamp'), value: displayText(spec.batterySoHTimestamp) },
    { label: translateLabel('Body Type'), value: displayText(spec.bodyType) },
    { label: translateLabel('Doors / Seats'), value: spec.numberOfDoors || spec.numberOfSeats ? `${displayText(spec.numberOfDoors)} / ${displayText(spec.numberOfSeats)}` : notAvailable() },
    { label: translateLabel('Vehicle Type'), value: displayText(spec.vehicleType) },
    { label: translateLabel('Country Of Origin'), value: displayText(spec.countryOfOrigin) },
    { label: translateLabel('Fuel Type'), value: displayText(spec.fuelType) },
    { label: translateLabel('Transmission'), value: displayText(spec.transmissionType) },
    { label: translateLabel('Max Power'), value: displayText(spec.maxPowerInKw) },
    { label: translateLabel('Paint Code'), value: displayText(spec.paintCode) },
    { label: translateLabel('Production Date'), value: displayText(spec.productionDate) },
    { label: translateLabel('Total Price'), value: typeof spec.totalPrice === 'number' ? formatPrice(spec.totalPrice) : displayText(spec.totalPrice) },
    { label: translateLabel('Wheels Code'), value: displayText(spec.wheelsCode) },
    { label: translateLabel('Number Of Gears'), value: displayText(spec.numberOfGears) },
    { label: translateLabel('Third Row Seats Code'), value: displayText(spec.thirdRowSeatsCode) },
    { label: translateLabel('Decor Code'), value: displayText(spec.decorCode) },
    { label: translateLabel('Rear Seats Name'), value: displayText(spec.rearSeatsName) },
    { label: translateLabel('Seats Name'), value: displayText(spec.seatsName) },
    { label: translateLabel('Towing Code'), value: displayText(spec.towingCode) },
  ];
  return cells.filter(cell => cell.value !== notAvailable());
});

const optionCodeRows = computed<InfoRow[]>(() => {
  return activeOptions.value.map(option => makeRow(option.code, displayText(option.displayName || option.code)));
});

const equipmentPriceRows = computed<InfoRow[]>(() => {
  const equipment = vehicleSpecsSource.value?.equipmentPrice || [];
  return equipment.map((item: any) => makeRow(item.name || item.code, typeof item.price === 'number' ? formatPrice(item.price) : displayText(item.price)));
});

const releaseVersionRows = computed<InfoRow[]>(() => {
  return releaseNotes.value.slice(0, 14).map(note => makeRow(note.title || translateLabel('Release Note'), displayText(note.customer_version)));
});

const releaseMetadataRows = computed<InfoRow[]>(() => {
  const relevant1 = releaseNotes.value[0];
  const relevant2 = releaseNotes.value[1];
  const relevant3 = releaseNotes.value[2];
  return [
    makeRow(translateLabel('Deployed Version'), displayText(releaseNotesEnvelope.value?.deployed_version)),
    makeRow(translateLabel('Staged Version'), displayText(releaseNotesEnvelope.value?.staged_version)),
    makeRow(translateLabel('Release Notes Version'), displayText(releaseNotesEnvelope.value?.release_notes_version)),
    makeRow(translateLabel('Relevant Note 1'), displayText(relevant1?.title)),
    makeRow(translateLabel('Relevant Note 2'), displayText(relevant2?.title)),
    makeRow(translateLabel('Relevant Note 3'), displayText(relevant3?.title)),
  ];
});

const releaseDetailRows = computed<InfoRow[]>(() => {
  const currentNote = releaseNotes.value[0];
  const historicalNote = releaseNotes.value.find(note => note.show_in_history) || releaseNotes.value[1];
  return [
    makeRow(translateLabel('Current Note Description'), displayText(currentNote?.description)),
    makeRow(translateLabel('Current Note Subtitle'), displayText((currentNote as any)?.subtitle)),
    makeRow(translateLabel('Current Note Show In History'), displayBool((currentNote as any)?.show_in_history)),
    makeRow(translateLabel('Current Note Icon'), displayText((currentNote as any)?.icon)),
    makeRow(translateLabel('Current Note Image URL'), displayText((currentNote as any)?.image_url), true),
    makeRow(translateLabel('Current Note Light Image URL'), displayText((currentNote as any)?.light_image_url), true),
    makeRow(translateLabel('Historical Note Subtitle'), displayText((historicalNote as any)?.subtitle)),
    makeRow(translateLabel('Historical Note Show In History'), displayBool((historicalNote as any)?.show_in_history)),
    makeRow(translateLabel('Historical Note Icon'), displayText((historicalNote as any)?.icon)),
    makeRow(translateLabel('Historical Note Description'), displayText(historicalNote?.description)),
    makeRow(translateLabel('Historical Note Image URL'), displayText((historicalNote as any)?.image_url), true),
    makeRow(translateLabel('Historical Note Light Image URL'), displayText((historicalNote as any)?.light_image_url), true),
  ];
});

const softwareStatusRows = computed<InfoRow[]>(() => ffRows('status'));

const glossaryKeys = [
  'batterySoh', 'batteryLevel', 'usableBatteryLevel', 'batteryCapacity',
  'ratedRange', 'estimatedRange', 'idealRange', 'chargeLimit',
  'chargingState', 'chargerPower', 'chargerVoltage', 'chargeEnergyAdded',
  'chargeRate', 'fastCharger', 'ccs', 'offPeakCharging',
  'scheduledDeparture', 'preconditioning', 'chargePort',
  'odometer', 'sentryMode', 'valetMode', 'dashcam', 'softwareVersion',
  'climateKeeper', 'temperature',
  'tpms',
  'basicWarranty', 'batteryWarranty', 'warrantyOdometer',
  'recentAlerts', 'chargingAlerts',
  'trim', 'vin', 'factoryOptions',
];

const glossaryItems = computed(() =>
  glossaryKeys.map(key => ({
    term: t(`glossary.${key}.term`),
    definition: t(`glossary.${key}.definition`),
  }))
);

function tabLabel(vin: string): string {
  // Try report data first
  const vehicleReport = store.vehicleReports[vin];
  if (vehicleReport?.vehicleData?.display_name) return vehicleReport.vehicleData.display_name;
  if (vehicleReport?.vehicle) return `${vehicleReport.vehicle.year} ${vehicleReport.vehicle.model}`;
  // Fall back to session inventory
  const sessionVehicle = store.sessionVehicles.find(v => v.vin === vin || String(v.id) === vin);
  if (sessionVehicle?.display_name) return sessionVehicle.display_name;
  if (sessionVehicle?.model) return `${sessionVehicle.year || ''} ${sessionVehicle.model}`.trim();
  if (sessionVehicle?.vin) return sessionVehicle.vin;
  return vin;
}

const currentReportHasData = computed(() => {
  return !!(report.value?.vehicleData || report.value?.vehicle);
});

function handlePhotoUpload(event: Event) {
  const input = event.target as HTMLInputElement;
  if (!input.files || !reportVin.value) return;
  const vin = reportVin.value;
  const remaining = 10 - store.getVehiclePhotos(vin).length;
  const files = Array.from(input.files).slice(0, remaining);

  files.forEach(file => {
    if (file.size > 5 * 1024 * 1024) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        store.addVehiclePhoto(vin, reader.result);
      }
    };
    reader.readAsDataURL(file);
  });

  input.value = '';
}

function handleRemovePhoto(index: number) {
  if (!reportVin.value) return;
  store.removeVehiclePhoto(reportVin.value, index);
}

const chargingFee = (session: ChargingSession): ChargingSessionFee | undefined => {
  return session.fees?.find(fee => fee.feeType === 'CHARGING');
};

const invoiceDownloadUrl = (invoiceId: string): string => {
  if (!store.sessionId || !reportVin.value) return '#';
  return getChargingInvoiceDownloadUrl(store.sessionId, reportVin.value, invoiceId);
};

const sessionEnergyLabel = (session: ChargingSession): string => {
  const fee = chargingFee(session);
  if (typeof fee?.usageBase === 'number') return `${fee.usageBase.toFixed(3)} kWh`;
  return notAvailable();
};

const sessionCostLabel = (session: ChargingSession): string => {
  const fee = chargingFee(session);
  if (!fee) return notAvailable();
  return `${fee.totalDue.toFixed(2)} ${fee.currencyCode}`;
};

const handleReportHome = () => {
  activeView.value = 'report';
};

const TRACE_STORAGE_KEY = 'cpt-trace-secret';
const cleanupTraces = () => {
  const hasTrace = typeof window !== 'undefined' && sessionStorage.getItem(TRACE_STORAGE_KEY);
  const sid = store.sessionId;
  if (hasTrace && sid) {
    const url = `${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/tesla/traces/${encodeURIComponent(sid)}`;
    try {
      fetch(url, { method: 'DELETE', headers: { 'X-Trace-Secret': hasTrace }, keepalive: true });
    } catch {
      // best-effort
    }
  }
};

onMounted(() => {
  window.addEventListener('cpt:report-home', handleReportHome as EventListener);
  window.addEventListener('beforeunload', cleanupTraces);
  expiryIntervalId.value = window.setInterval(() => {
    nowMs.value = Date.now();
  }, 1000);
});

onBeforeUnmount(() => {
  window.removeEventListener('cpt:report-home', handleReportHome as EventListener);
  window.removeEventListener('beforeunload', cleanupTraces);
  if (expiryIntervalId.value !== null) {
    window.clearInterval(expiryIntervalId.value);
  }
  cleanupTraces();
});

const downloadPdfBlobToFile = (pdfBlob: Blob) => {
  const url = window.URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `tesla-report-${reportVin.value || 'unknown'}-${Date.now()}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const handleDownloadReceipt = async () => {
  const paymentId = store.paymentId;
  if (!paymentId || isDownloadingReceipt.value || receiptDownloaded.value) return;
  isDownloadingReceipt.value = true;
  // GA4 receipt download (spec 049-google-analytics, Task 3.5). No-op until consent.
  trackEvent('file_download', { file_type: 'receipt' });
  try {
    const blob = await downloadReceiptPdf(paymentId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${paymentId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    receiptDownloaded.value = true;
  } catch (err) {
    store.setError(err instanceof Error ? err.message : 'Failed to download receipt');
  } finally {
    isDownloadingReceipt.value = false;
  }
};

const generatePdfViaSyncEndpoint = async (photos: string[], sessionId: string) => {
  const pdfBlob = await generatePdfReport(report.value!, locale.value, photos, sessionId);
  pdfProgress.value = 100;
  downloadPdfBlobToFile(pdfBlob);
};

const handleSavePdf = async () => {
  if (!report.value) {
    store.setError(t('report.noReportDataAvailable'));
    return;
  }

  if (!report.value.vehicle || !report.value.vehicleData) {
    store.setError(t('report.incompleteReportData'));
    return;
  }

  // GA4 report PDF download (spec 049-google-analytics, Task 3.5). No-op until consent.
  trackEvent('file_download', { file_type: 'report' });

  isGeneratingPdf.value = true;
  pdfProgress.value = 10;
  store.setError(null);

  try {
    const photos = reportVin.value ? store.getVehiclePhotos(reportVin.value) : [];
    const sessionId = store.sessionId;
    if (!sessionId) {
      throw new Error(t('report.noReportDataAvailable'));
    }

    let downloadUrl = '';
    let completedReportId = '';

    try {
      const asyncResult = await generatePdfReportAsync(report.value, locale.value, photos, sessionId);
      if (!asyncResult || !asyncResult.task_id) {
        throw new Error(t('report.failedToGeneratePdf'));
      }

      const { task_id } = asyncResult;
      pdfProgress.value = 30;

      let status = 'processing';
      let attempts = 0;
      const maxAttempts = 60;

      while (status === 'processing' && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts += 1;

        const result = await getReportTaskStatus(task_id, sessionId);
        status = result.status;

        if (status === 'complete') {
          downloadUrl = result.download_url || '';
          completedReportId = result.report_id || '';
          pdfProgress.value = 100;
        } else if (status === 'failed') {
          throw new Error(result.error || t('report.failedToGeneratePdf'));
        } else {
          pdfProgress.value = Math.min(95, 30 + (attempts * 2));
        }
      }

      if (status !== 'complete') {
        throw new Error(t('report.pdfGenerationTimedOut'));
      }
    } catch (asyncError) {
      console.warn('Async PDF generation failed, falling back to sync download:', asyncError);
      pdfProgress.value = 35;
      await generatePdfViaSyncEndpoint(photos, sessionId);
      return;
    }

    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
      if (completedReportId) {
        try {
          await deleteReport(completedReportId);
        } catch (cleanupErr) {
          console.warn('Report cleanup failed (non-fatal):', cleanupErr);
        }
      }
    } else {
      await generatePdfViaSyncEndpoint(photos, sessionId);
    }
  } catch (error) {
    console.error('PDF generation error:', error);
    const errorMessage = error instanceof Error ? error.message : t('report.failedToGeneratePdf');
    store.setError(errorMessage);
  } finally {
    isGeneratingPdf.value = false;
    pdfProgress.value = 0;
  }
};
</script>
